import type { ThemeDefinition } from "vuetify";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { pt } from "vuetify/locale";
import { TEACHPLUS_THEME_COLORS } from "~/utils/schoolBrand";

const teachPlusTheme: ThemeDefinition = {
  dark: false,
  colors: {
    background: "#FFFFFF",
    surface: "#FFFFFF",
    primary: TEACHPLUS_THEME_COLORS.primary,
    "primary-darken-1": TEACHPLUS_THEME_COLORS.primaryDarken,
    secondary: TEACHPLUS_THEME_COLORS.secondary,
    "secondary-darken-1": TEACHPLUS_THEME_COLORS.secondaryDarken,
    error: "#B00020",
    info: "#2196F3",
    success: "#4CAF50",
    warning: "#FB8C00",
  },
};

export default defineNuxtPlugin<{
  vuetify: ReturnType<typeof createVuetify>;
}>({
  name: "vuetify",
  setup(nuxtApp) {
    const vuetify = createVuetify({
      locale: {
        locale: "pt",
        messages: { pt },
      },
      display: {
        mobileBreakpoint: "lg",
      },
      defaults: {
        VTextField: {
          variant: "underlined",
        },
        VSelect: {
          variant: "underlined",
        },
      },
      theme: {
        defaultTheme: "teachPlusTheme",
        themes: {
          teachPlusTheme,
          // Neutral base for public school themes; never follows the session.
          publicAssessmentTheme: {
            ...teachPlusTheme,
            colors: { ...teachPlusTheme.colors },
          },
        },
      },
      ssr: false,
      components,
      directives,
    });

    nuxtApp.vueApp.use(vuetify);

    if (import.meta.client) {
      watch(
        vuetify.theme.styles,
        (css) => {
          // Nuxt's head manager defers its DOM update. Commit Vuetify's own
          // stylesheet before rendering consumers so color transitions never
          // start from the previous school's palette. Initial insertion and
          // ownership remain with Vuetify and the head manager.
          const stylesheet = document.getElementById(
            "vuetify-theme-stylesheet",
          );
          if (stylesheet) stylesheet.textContent = css;
        },
        { flush: "sync" },
      );
    }

    return { provide: { vuetify } };
  },
});
